// @flow

import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import { absolute, Color, flex, heights, hoverChild, topMargin, Width } from 'styles';
import { ImageLoader, Link } from 'components/Gui';
import { useBreakpoints, useImages } from 'hooks';
import { graphql, useStaticQuery } from 'gatsby';
import { RightTriangleArrow } from 'components/Svg';
import type { AllImages } from 'hooks/use-images';
import { ModalContextActions, useModalDispatch } from 'contexts';
import { VIDEO_PLAYER } from 'Constants';

const Wrapper = styled.div`
  ${({ marginTop }) => marginTop && topMargin(...marginTop)};
  width: 100%;
  position: relative;
  ${({ dotsWrapper }) =>
    dotsWrapper &&
    css`
      box-sizing: border-box;
      padding: 36px 24px;

      @media print, screen and (min-width: ${Width.BREAK_XL}px) {
        padding: 54px 28px;
      }
    `};
`;

const Image = styled.div`
  position: relative;
  width: 100%;
  ${({ noShadow }) => !noShadow && `box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15)`};
  ${({ imageHeights }) => imageHeights && heights(...imageHeights)}
`;

const Squares = styled.div`
  ${absolute()};

  > div {
    position: absolute;
    width: 116px;

    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      width: 202px;
    }
    &:nth-of-type(1) {
      top: 0;
      left: 0;
    }
    &:nth-of-type(2) {
      bottom: 0;
      right: 0;
    }
  }
`;

const OpenModal = styled(Link)`
  ${absolute()};
  width: 100%;
  ${flex()};
  ${hoverChild('span.background', 'opacity', 0, 1)};
  ${hoverChild('span.arrow', 'background', Color.YELLOW_MELLOW, Color.PURPLE_FIGURE)};

  > span.background {
    ${absolute()};
    background: rgba(0, 0, 0, 0.2);
  }

  > span.arrow {
    ${flex()};
    width: ${({ smallButton }) => (smallButton ? 48 : 65)}px;
    height: ${({ smallButton }) => (smallButton ? 48 : 65)}px;
    border-radius: 50%;
    opacity: 0.9;

    svg {
      transform: translateX(1px);
      width: ${({ smallButton }) => (smallButton ? 9 : 13)}px;
    }
  }
`;

type Props = {
  src: string,
  posterName: string,
  marginTop?: number[],
  imageHeights?: Array<number | string>,
  dotsWrapper?: boolean,
  imageBreakpoints?: { [key: string]: string },
  imageNames?: { [key: string | 'default']: string },
  imageName?: string,
  smallButton?: boolean,
  noShadow?: boolean,
  images: AllImages,
};

const BlockyModalVideoPlayer = ({
  src,
  posterName,
  marginTop,
  imageHeights,
  dotsWrapper = true,
  smallButton = false,
  noShadow = false,
  images,
  imageBreakpoints,
  imageNames,
  imageName,
  ...rest
}: Props) => {
  const squaresImages = useStaticQuery(graphql`
    query {
      otherImage: allFile(filter: { relativeDirectory: { eq: "global/blocky" }, extension: { in: ["svg", "gif"] } }) {
        edges {
          node {
            ...OtherImage
          }
        }
      }
    }
  `);
  const breakpoint = useBreakpoints();
  const imageBreakpoint = useBreakpoints(imageBreakpoints);
  const getSquaresImage = useImages(squaresImages);
  const getImage = useImages(images);
  const squaresImageName = `squares-${breakpoint === 'desktop' ? 'large' : 'small'}`;
  const image = imageBreakpoints ? (imageBreakpoint ? imageNames[imageBreakpoint] : imageNames.default) : imageName;
  const dispatch = useModalDispatch();
  const openModal = useCallback(() => {
    dispatch({
      type: ModalContextActions.SET_VIDEO_SRC,
      payload: {
        src,
        poster: posterName ? getImage(posterName)?.childImageSharp?.original?.src : '',
      },
    });
    dispatch({
      type: ModalContextActions.OPEN,
      payload: VIDEO_PLAYER,
    });
  }, [src, posterName, getImage, dispatch]);
  return (
    <Wrapper dotsWrapper={dotsWrapper} marginTop={marginTop} {...rest}>
      {dotsWrapper && (
        <>
          <Squares>
            <div>
              <ImageLoader image={getSquaresImage(squaresImageName)} />
            </div>
            <div>
              <ImageLoader image={getSquaresImage(squaresImageName)} />
            </div>
          </Squares>
        </>
      )}
      <Image imageHeights={imageHeights} noShadow={noShadow}>
        <ImageLoader fitContainer={!!imageHeights} image={getImage(image)} />
        <OpenModal smallButton={smallButton} onClick={openModal}>
          <span className="background" />
          <span className="arrow">{RightTriangleArrow}</span>
        </OpenModal>
      </Image>
    </Wrapper>
  );
};

BlockyModalVideoPlayer.contentType = 'BlockyModalVideoPlayer';

export default BlockyModalVideoPlayer;
